import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

import { auditBookmarkHealth } from '../lib/linkHealthSentinel';
import { findStaleBookmarks } from '../lib/staleArchiver';
import { swapBookmarkToWayback } from '../lib/deadLinkSwapper';
import DuplicateResolverModal from './DuplicateResolverModal';

const { FiTrash2, FiAlertTriangle, FiCheckCircle2, FiX, FiRefreshCw, FiExternalLink, FiShield, FiSliders, FiArchive, FiGitMerge } = FiIcons;

export default function AuditModal({
  isOpen,
  onClose,
  data,
  onDeleteBookmark,
  onFixBookmark,
  accentColor = '#c2ffe0',
}) {
  const [cleaning, setCleaning] = useState(false);
  const [resolvingPair, setResolvingPair] = useState(null);

  const handleResolveMerge = async (origId, dupId, mergedData) => {
    if (onFixBookmark) await onFixBookmark(origId, mergedData);
    await onDeleteBookmark(dupId);
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose?.();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Analyze all bookmarks for duplicates and anomalies
  const auditResults = useMemo(() => {
    const all = [];
    Object.values(data).forEach((folder) => {
      (folder.items || []).forEach((item) => {
        if (item.type === 'link') {
          all.push({
            ...item,
            folderTitle: folder.id === 'root' ? 'Home' : folder.title,
            folderId: folder.id,
          });
        }
      });
    });

    const urlMap = new Map();
    const duplicates = [];

    all.forEach((b) => {
      const normalizedUrl = (b.url || '').trim().toLowerCase().replace(/\/+$/, '');
      if (urlMap.has(normalizedUrl)) {
        duplicates.push({
          original: urlMap.get(normalizedUrl),
          duplicate: b,
        });
      } else {
        urlMap.set(normalizedUrl, b);
      }
    });

    const healthAudit = auditBookmarkHealth(all);
    const staleBookmarks = findStaleBookmarks(all);

    const totalCount = all.length;
    const issuesCount = duplicates.length + healthAudit.insecureHttp.length + healthAudit.clutteredTracking.length;
    const healthScore = totalCount > 0 ? Math.max(0, Math.round(((totalCount - issuesCount) / totalCount) * 100)) : 100;

    return {
      totalCount,
      duplicates,
      insecureHttp: healthAudit.insecureHttp,
      clutteredTracking: healthAudit.clutteredTracking,
      staleBookmarks,
      healthScore,
    };
  }, [data]);

  async function handleArchiveStale(item) {
    if (onFixBookmark) {
      const currentNote = item.note || '';
      const newNote = currentNote ? `${currentNote} #archive` : '#archive';
      await onFixBookmark(item.id, { note: newNote });
    }
  }

  async function handleSwapWayback(item) {
    if (onFixBookmark) {
      const swapped = swapBookmarkToWayback(item);
      await onFixBookmark(item.id, swapped);
    }
  }

  async function handleCleanAllDuplicates() {
    setCleaning(true);
    try {
      for (const item of auditResults.duplicates) {
        await onDeleteBookmark(item.duplicate.id);
      }
    } finally {
      setCleaning(false);
    }
  }

  async function handleUpgradeHttps(item, fixedUrl) {
    if (onFixBookmark) {
      await onFixBookmark(item.id, { url: fixedUrl });
    }
  }

  async function handleStripTelemetry(item, fixedUrl) {
    if (onFixBookmark) {
      await onFixBookmark(item.id, { url: fixedUrl });
    }
  }

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-black/75 backdrop-blur-md"
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.94, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.94, y: 15 }}
          transition={{ duration: 0.2 }}
          className="relative w-full max-w-lg rounded-2xl bg-[#141418] border border-[#26262e] shadow-2xl p-6 z-10 max-h-[85vh] flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between pb-4 border-b border-[#24242c] shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-400">
                <SafeIcon icon={FiAlertTriangle} className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base font-bold text-white">Library Health & Duplicate Audit</h3>
                <p className="text-xs text-[#777]">
                  Scanned {auditResults.totalCount} total bookmarks
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-[#666] hover:text-white hover:bg-[#222] transition-colors"
            >
              <SafeIcon icon={FiX} className="w-4 h-4" />
            </button>
          </div>

          {/* Health Score Pill */}
          <div className="py-4 flex items-center justify-between px-4 my-4 rounded-xl bg-[#1a1a22] border border-[#2c2c38] shrink-0">
            <div>
              <div className="text-xs text-[#888] font-medium uppercase tracking-wider">Library Cleanliness</div>
              <div className="text-xl font-bold text-white flex items-center gap-2">
                <span>{auditResults.healthScore}% Healthy</span>
                {auditResults.healthScore === 100 && (
                  <SafeIcon icon={FiCheckCircle2} className="w-4 h-4 text-emerald-400" />
                )}
              </div>
            </div>
            {auditResults.duplicates.length > 0 && (
              <button
                onClick={handleCleanAllDuplicates}
                disabled={cleaning}
                className="px-3.5 py-2 rounded-xl text-xs font-semibold text-white bg-red-500/80 hover:bg-red-500 transition-colors flex items-center gap-1.5 shadow-lg shadow-red-500/20"
              >
                <SafeIcon icon={FiTrash2} className="w-3.5 h-3.5" />
                <span>{cleaning ? 'Cleaning...' : `Remove All ${auditResults.duplicates.length} Duplicates`}</span>
              </button>
            )}
          </div>

          {/* Issue List */}
          <div className="flex-1 overflow-y-auto space-y-4 pr-1">
            {auditResults.duplicates.length === 0 && auditResults.insecureHttp.length === 0 ? (
              <div className="py-12 text-center text-[#777] space-y-2">
                <SafeIcon icon={FiCheckCircle2} className="w-10 h-10 text-emerald-400 mx-auto" />
                <p className="text-sm font-semibold text-white">Your library is 100% clean!</p>
                <p className="text-xs text-[#555]">No duplicate links or protocol issues detected.</p>
              </div>
            ) : (
              <>
                {/* Duplicates Section */}
                {auditResults.duplicates.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-amber-400 uppercase tracking-wider mb-2">
                      Duplicate Bookmarks ({auditResults.duplicates.length})
                    </h4>
                    <div className="space-y-2">
                      {auditResults.duplicates.map(({ original, duplicate }) => (
                        <div
                          key={duplicate.id}
                          className="p-3 rounded-xl bg-[#17171e] border border-[#24242e] flex items-center justify-between gap-3 text-xs"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="font-semibold text-white truncate">{duplicate.title}</div>
                            <div className="text-[11px] text-[#666] truncate">{duplicate.url}</div>
                            <div className="text-[10px] text-[#555] mt-1">
                              In folder: <span className="text-[#aaa]">{duplicate.folderTitle}</span> (Duplicate of link in {original.folderTitle})
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            <button
                              onClick={() => setResolvingPair({ original, duplicate })}
                              className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-semibold bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/30 transition-all"
                              title="Open Side-by-Side Resolution Wizard"
                            >
                              <SafeIcon icon={FiGitMerge} className="w-2.5 h-2.5" />
                              <span>Resolve</span>
                            </button>
                            <button
                              onClick={() => onDeleteBookmark(duplicate.id)}
                              className="p-1.5 rounded-lg text-red-400 hover:bg-red-500/10 transition-colors"
                              title="Delete this duplicate"
                            >
                              <SafeIcon icon={FiTrash2} className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Insecure HTTP Section */}
                {auditResults.insecureHttp.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-amber-400 uppercase tracking-wider mb-2">
                      Unencrypted HTTP Links ({auditResults.insecureHttp.length})
                    </h4>
                    <div className="space-y-2">
                      {auditResults.insecureHttp.slice(0, 10).map(({ item: b, issue, suggestion, fixedUrl }) => (
                        <div
                          key={b.id}
                          className="p-2.5 rounded-xl bg-[#17171e] border border-[#22222a] flex items-center justify-between text-xs text-[#888] gap-2"
                        >
                          <div className="truncate min-w-0 pr-2">
                            <span className="text-white font-medium">{b.title}</span> • <span className="font-mono text-[#aaa]">{b.url}</span>
                          </div>
                          <button
                            onClick={() => handleUpgradeHttps(b, fixedUrl)}
                            className="text-[10px] font-semibold px-2 py-1 rounded-lg bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/30 transition-all shrink-0"
                            title="Upgrade URL protocol to HTTPS"
                          >
                            Upgrade HTTPS
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Cluttered Tracking Section */}
                {auditResults.clutteredTracking.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-purple-400 uppercase tracking-wider mb-2">
                      Telemetry Tracking Clutter ({auditResults.clutteredTracking.length})
                    </h4>
                    <div className="space-y-2">
                      {auditResults.clutteredTracking.slice(0, 10).map(({ item: b, fixedUrl }) => (
                        <div
                          key={b.id}
                          className="p-2.5 rounded-xl bg-[#17171e] border border-[#22222a] flex items-center justify-between text-xs text-[#888] gap-2"
                        >
                          <div className="truncate min-w-0 pr-2">
                            <span className="text-white font-medium">{b.title}</span> • <span className="font-mono text-[#aaa]">{b.url}</span>
                          </div>
                          <button
                            onClick={() => handleStripTelemetry(b, fixedUrl)}
                            className="text-[10px] font-semibold px-2 py-1 rounded-lg bg-purple-500/20 text-purple-300 border border-purple-500/40 hover:bg-purple-500/30 transition-all shrink-0"
                            title="Strip tracking query parameters"
                          >
                            Strip Telemetry
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Stale Inactive Links Section */}
                {auditResults.staleBookmarks?.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-sky-400 uppercase tracking-wider mb-2">
                      Stale Inactive Bookmarks ({auditResults.staleBookmarks.length})
                    </h4>
                    <div className="space-y-2">
                      {auditResults.staleBookmarks.slice(0, 10).map((b) => (
                        <div
                          key={b.id}
                          className="p-2.5 rounded-xl bg-[#17171e] border border-[#22222a] flex items-center justify-between text-xs text-[#888] gap-2"
                        >
                          <div className="truncate min-w-0 pr-2">
                            <span className="text-white font-medium">{b.title}</span> • <span className="font-mono text-[#777]">{b.clicks || 0} clicks</span>
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            <button
                              onClick={() => handleSwapWayback(b)}
                              className="text-[10px] font-semibold px-2 py-1 rounded-lg bg-amber-500/20 text-amber-300 border border-amber-500/40 hover:bg-amber-500/30 transition-all shrink-0 flex items-center gap-1"
                              title="Swap URL with verified Wayback Machine snapshot"
                            >
                              <span>🏛 Swap Wayback</span>
                            </button>
                            <button
                              onClick={() => handleArchiveStale(b)}
                              className="text-[10px] font-semibold px-2 py-1 rounded-lg bg-sky-500/20 text-sky-300 border border-sky-500/40 hover:bg-sky-500/30 transition-all shrink-0 flex items-center gap-1"
                              title="Tag as #archive"
                            >
                              <SafeIcon icon={FiArchive} className="w-2.5 h-2.5" />
                              <span>Tag #archive</span>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </motion.div>
      </div>

      <DuplicateResolverModal
        isOpen={!!resolvingPair}
        onClose={() => setResolvingPair(null)}
        pair={resolvingPair}
        onResolveMerge={handleResolveMerge}
        onDeleteDuplicate={onDeleteBookmark}
      />
    </AnimatePresence>
  );
}
